import { useDrawerStore } from '../../../store';
import {
  BaseModel,
  FormField,
  PageableResponse,
  WarehouseModel,
} from '../../../types';
import FormDrawer from '../../../components/ui/FormDrawer';
import {
  useCreateData,
  useFetchData,
  useLocationStore,
  useUpdateData,
} from '../../../hooks';
import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { Resource } from '../../../lib/enums';
import { LocationSchema } from '../../../lib/schemas';
import ErrorModal from '../../../components/ui/modal/ErrorModal';

const LocationForm = ({ api }: { api: string }) => {
  const drawerStore = useDrawerStore();
  const locationStore = useLocationStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const { data: warehouseData, isLoading: isWarehouseLoading } = useFetchData<
    PageableResponse<WarehouseModel>
  >('/location/warehouses', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const {
    mutate,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
  } = useCreateData(api);
  const {
    mutate: mutateUpdate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = useUpdateData(api);

  const onFinish = (values: { warehouse: string }) => {
    setSpinning(true);
    const warehouse: BaseModel | undefined = warehouseData?.data?.find(
      (obj) => {
        return obj.id == values.warehouse;
      },
    );
    const jsonData = {
      ...values,
      warehouse: warehouse,
    };
    if (locationStore.data !== null) {
      const oldData = LocationSchema.parse(locationStore.data);

      const updatedData = {
        ...oldData,
        ...jsonData,
      };
      mutateUpdate({ id: oldData.id, form: updatedData });
    } else {
      mutate(jsonData);
    }
  };

  const formFields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      rules: [{ required: true, message: 'Please enter Code' }],
      disabled: locationStore.data != null,
      defaultValue: locationStore.data?.code,
    },
    {
      name: 'description',
      label: 'Description',
      rules: [{ required: true, message: 'Please enter description' }],
      defaultValue: locationStore.data?.description,
    },
    {
      name: 'warehouse',
      label: 'Warehouse',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isWarehouseLoading,
      values: warehouseData?.data,
      rules: [{ required: true, message: 'Please select warehouse' }],
      defaultValue: locationStore.data?.warehouse.id,
    },
  ];

  useEffect(() => {
    if (isSuccessCreate || isSuccessUpdate || isErrorUpdate || isErrorCreate)
      setSpinning(false);

    if (isErrorUpdate) {
      setInfoModalOpen(true);
      setErrorMessage(errorUpdate?.response?.data.message);
    }
    if (isErrorCreate) {
      setInfoModalOpen(true);
      setErrorMessage(errorCreate?.response?.data.message);
    }
  }, [
    isSuccessCreate,
    isSuccessUpdate,
    isErrorUpdate,
    isErrorCreate,
    errorCreate,
  ]);

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <FormDrawer
        isOpen={drawerStore.isOpen}
        onClose={drawerStore.onClose}
        data={locationStore.data}
        moduleName={'Location'}
        setData={locationStore.setData}
        onFinish={onFinish}
        formFields={formFields}
      />
      <ErrorModal
        visible={isInfoModalOpen}
        message={errorMessage}
        onClose={() => setInfoModalOpen(false)}
      />
    </>
  );
};

export default LocationForm;
